/* eslint-disable new-cap */
import React from 'react';
import qs from 'querystringify';
import { useStoreState } from 'easy-peasy';
import { toast } from 'react-toastify';

import Layout from '../../../../components/global/Layout';
import { Pagination } from '../../../../components/elements';
import Header from './Header';
import Search from './Search';
import BookingTransaction from './BookingTransaction';
import {
  useTableBookingSummaryData,
  usePlaceTableBookingStatistics,
  usePlaceData,
  useTableBookingSummaryCSVData,
  usePlaceTableBookingStatisticsCSVData,
} from '../helpers';
import PlaceBookingStatistics from './PlaceBookingStatistics';
import { downloadCSVFile } from '../../../OrderManagement/OrderTransactions/helpers';

const setStartDateHours = dateObject => {
  dateObject.setHours(0, 0, 0, 0);
  return dateObject;
};

const setEndDateHours = dateObject => {
  dateObject.setHours(23, 59, 59, 999);
  return dateObject;
};

const startDateISOString = () => {
  const startDate = setStartDateHours(new Date());
  // startDate.setDate(startDate.getDate() - 90);
  return startDate.toISOString();
};

const endDateISOString = () => {
  const endDate = setEndDateHours(new Date());
  return endDate.toISOString();
};

const LIST_SIZE = 50;

const TableBookingTransaction = ({ history, location, match }) => {
  const { userId: adminUserId } = useStoreState(state => state.auth);
  const { placeId } = match.params;

  const routeState = qs.parse(location.search);

  const [downloadCsv, setDownloadCsv] = React.useState(false);
  const [downloadPDF, setDownloadPDF] = React.useState(false);
  const [bookingDateSort, setBookingDateSort] = React.useState('AES');
  const [sort, setSort] = React.useState('BOOKING_DATE_ASC');

  const [startDateFilterRef, setStartDateRef] = React.useState();
  const [endDateFilterRef, setEndDateRef] = React.useState();

  const { page, bookingName, bookingReference } = routeState;

  let { dateFilterDisabled, nameEnabled, refEnabled } = routeState;

  if (dateFilterDisabled) {
    dateFilterDisabled = dateFilterDisabled === 'true';
  } else {
    dateFilterDisabled = true;
  }

  if (nameEnabled) {
    nameEnabled = nameEnabled === 'true';
  } else {
    nameEnabled = false;
  }

  if (refEnabled) {
    refEnabled = refEnabled === 'true';
  } else {
    refEnabled = false;
  }

  let startDateFilter;
  let endDateFilter;

  if (startDateFilterRef) {
    startDateFilter = startDateFilterRef;
  } else {
    startDateFilter = startDateISOString();
  }

  if (endDateFilterRef) {
    endDateFilter = endDateFilterRef;
  } else {
    endDateFilter = endDateISOString();
  }

  const currentPage = parseInt(page, 10) - 1 >= 0 ? parseInt(page, 10) - 1 : 0;

  const handleChange = args => {
    history.push(
      `/table-booking-transaction/${placeId}${qs.stringify(
        {
          ...routeState,
          page: 1,
          ...args,
        },
        true,
      )}`,
    );
  };

  const [
    loadingTableBookingDashboardData,
    tableBookingDashboardData,
    totalSizeTableBookingDashboardData,
    totalPagesTableBookingDashboardData,
  ] = useTableBookingSummaryData({
    adminUserId,
    from: currentPage * LIST_SIZE,
    size: LIST_SIZE,
    city: null,
    placeId,
    startDate: startDateFilter,
    endDate: endDateFilter,
    dateFilterDisabled,
    bookingName,
    bookingReference,
    sort,
  });

  const placeData = usePlaceData({ placeId });

  const [
    loadingPlaceTableBookingStatistics,
    placeTableBookingStatistics,
  ] = usePlaceTableBookingStatistics({
    adminUserId,
    from: currentPage * LIST_SIZE,
    size: LIST_SIZE,
    city: null,
    placeId,
    startDate: startDateFilter,
    endDate: endDateFilter,
    dateFilterDisabled,
  });

  const [
    loadingTableBookingDashboardDataCSV,
    tableBookingDashboardDataCSV,
  ] = useTableBookingSummaryCSVData({
    adminUserId,
    from: currentPage * LIST_SIZE,
    size: LIST_SIZE,
    city: null,
    placeId,
    startDate: startDateFilter,
    endDate: endDateFilter,
    dateFilterDisabled,
    bookingName,
    bookingReference,
    sort,
  });

  const [
    loadingPlaceTableBookingStatisticsCSV,
    placeTableBookingStatisticsCSV,
  ] = usePlaceTableBookingStatisticsCSVData({
    adminUserId,
    from: currentPage * LIST_SIZE,
    size: LIST_SIZE,
    city: null,
    placeId,
    startDate: startDateFilter,
    endDate: endDateFilter,
    dateFilterDisabled,
  });

  const onDownloadCSV = (content, prefix) => {
    try {
      setDownloadCsv(true);
      downloadCSVFile(content, `${prefix}-${new Date().toISOString()}.csv`);
    } catch (error) {
      toast.error(
        `Failed to download CSV: ${error.message || 'Unknown error'}`,
      );
    } finally {
      setDownloadCsv(false);
    }
  };

  const downloadPdf = () => {
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'landscape' });

    const content2 = placeTableBookingStatisticsCSV;

    // doc.text('Transactions Summary');

    doc.autoTable({
      head: [content2.headers],
      body: content2.rawData.map(Object.values),
      styles: { fontSize: 8 },
      headStyles: { fontSize: 7 },
    });

    const content1 = tableBookingDashboardDataCSV;

    // doc.text('Transactions Data');

    doc.autoTable({
      head: [content1.headers],
      body: content1.rawData.map(Object.values),
      styles: { fontSize: 8 },
      headStyles: { fontSize: 7 },
      columnStyles: {
        0: {
          cellWidth: 20,
          overflow: 'hidden',
        },
        1: {
          cellWidth: 20,
        },
        2: {
          cellWidth: 20,
          overflow: 'hidden',
        },
        3: {
          cellWidth: 20,
          overflow: 'hidden',
        },
        4: { cellWidth: 30, overflow: 'hidden' },
        6: { cellWidth: 20, overflow: 'hidden' },
      },
    });

    doc.save(
      `${placeData?.name
        ?.replaceAll(' ', '-')
        .toLowerCase()}-booking-summary-${new Date().toISOString()}.pdf`,
    );
  };

  const onDownloadPDF = (content, prefix) => {
    try {
      setDownloadPDF(true);
      downloadPdf(content, `${prefix}-${new Date().toISOString()}.csv`);
    } catch (error) {
      toast.error(
        `Failed to download PDF: ${error.message || 'Unknown error'}`,
      );
    } finally {
      setDownloadPDF(false);
    }
  };

  return (
    <Layout>
      <Header history={history} placeData={placeData} />
      <PlaceBookingStatistics
        loading={loadingPlaceTableBookingStatistics}
        placeBookingStatistics={placeTableBookingStatistics}
      />
      <Search
        bookingDateFilter={null}
        handleChange={handleChange}
        dateFilterDisabled={dateFilterDisabled}
        setStartDateRef={setStartDateRef}
        setEndDateRef={setEndDateRef}
        nameEnabled={nameEnabled}
        refEnabled={refEnabled}
        bookingName={bookingName}
        bookingReference={bookingReference}
      />
      {!loadingTableBookingDashboardDataCSV &&
        !loadingPlaceTableBookingStatisticsCSV && (
          <button
            className={`${downloadCsv &&
              'is-loading'} button is-primary is-outlined`}
            style={{ margin: '5px' }}
            onClick={async () => {
              onDownloadCSV(
                tableBookingDashboardDataCSV?.contentCSV,
                `${placeData?.name
                  ?.replaceAll(' ', '-')
                  .toLowerCase()}-booking-summary`,
              );
              onDownloadCSV(
                placeTableBookingStatisticsCSV?.contentCSV,
                `${placeData?.name
                  ?.replaceAll(' ', '-')
                  .toLowerCase()}-booking-summary-dashboard`,
              );
            }}
          >
            Download CSV
          </button>
        )}
      {!loadingTableBookingDashboardDataCSV &&
        !loadingPlaceTableBookingStatisticsCSV && (
          <button
            className={`${downloadPDF &&
              'is-loading'} button is-primary is-outlined`}
            style={{ margin: '5px' }}
            onClick={async () => {
              onDownloadPDF();
            }}
          >
            Download PDF
          </button>
        )}
      {totalSizeTableBookingDashboardData > 0 && (
        <Pagination
          pageFrom={currentPage * LIST_SIZE}
          pageCount={totalPagesTableBookingDashboardData}
          listSize={LIST_SIZE}
          totalElements={totalSizeTableBookingDashboardData}
          currentPage={currentPage}
          handlePageClick={value => {
            history.push(
              `/table-booking-transaction/${placeId}${qs.stringify(
                {
                  ...routeState,
                  page: value.selected + 1,
                },
                true,
              )}`,
            );
          }}
        />
      )}
      <BookingTransaction
        loading={loadingTableBookingDashboardData}
        transactionData={tableBookingDashboardData}
        history={history}
        bookingDateSort={bookingDateSort}
        setBookingDateSort={setBookingDateSort}
        setSort={setSort}
        placeId={placeId}
      />
    </Layout>
  );
};

export default TableBookingTransaction;
